import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, Download, Loader2, Undo2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/folio-tags`;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const Row = ({ r, busy, decide }) => (
  <tr className="border-t border-slate-100 text-sm" data-testid={`payee-change-row-${r.folio_no}`}>
    <td className="px-3 py-2"><div className="font-medium text-slate-800">{r.client_name || "—"}</div><div className="text-xs text-slate-500">{r.pan}</div></td>
    <td className="px-3 py-2 font-mono text-xs">{r.folio_no}<div className="text-[11px] text-slate-400">{r.source}</div></td>
    <td className="px-3 py-2"><span className="font-mono text-xs">{r.previous || "—"}</span><div className="text-xs text-slate-500">{r.previous_name}</div></td>
    <td className="px-3 py-2"><span className="font-mono text-xs text-amber-700">{r.current}</span><div className="text-xs text-slate-500">{r.current_name}</div></td>
    <td className="px-3 py-2 text-xs text-slate-600">{r.reason}</td>
    <td className="px-3 py-2 text-right">
      {r.decision ? (
        <span className="text-xs font-medium text-teal-700" data-testid={`payee-change-decision-${r.folio_no}`}>{r.decision === "keep" ? "Kept new" : "Reverted"}</span>
      ) : (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" disabled={busy || !r.previous} onClick={() => decide(r, "revert")} data-testid={`payee-change-revert-${r.folio_no}`}>
            <Undo2 className="mr-1 h-3.5 w-3.5" />Revert to {r.previous.split(",")[0] || "—"}
          </Button>
          <Button size="sm" className="bg-[#1a7f6f] hover:bg-[#166a5c]" disabled={busy} onClick={() => decide(r, "keep")} data-testid={`payee-change-keep-${r.folio_no}`}>
            <Check className="mr-1 h-3.5 w-3.5" />Keep {r.current}
          </Button>
        </div>
      )}
    </td>
  </tr>
);

export const SplitFoliosDownload = () => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/reports/split-folios.xlsx`, { headers: hdr() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const url = URL.createObjectURL(await r.blob());
      Object.assign(document.createElement("a"), { href: url, download: "folios_not_with_client_associate.xlsx" }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed", { description: e.message }); } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={go} disabled={busy} data-testid="download-split-folios-btn">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Folios Not With Client's Associate
    </Button>
  );
};

export const PayeeChangesReview = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => fetch(`${API}/payee-changes`, { headers: hdr() }).then((r) => r.json()).then(setData).catch(() => setData({ rows: [], pending: 0 }));
  useEffect(() => { load(); }, []);
  const decide = async (r, decision) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/payee-changes/decide`, { method: "POST", headers: hdr(), body: JSON.stringify({ folio_no: r.folio_no, period: r.period, previous: r.previous, decision }) });
      if (!res.ok) throw new Error((await res.json()).detail || `HTTP ${res.status}`);
      toast.success(decision === "keep" ? `Kept ${r.current} for ${r.folio_no}` : `Reverted ${r.folio_no} to ${r.previous}`);
      await load();
    } catch (e) { toast.error("Could not save", { description: e.message }); } finally { setBusy(false); }
  };
  const pending = data?.pending || 0;
  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); load(); }} data-testid="review-payee-changes-btn"
        className={pending ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : ""}>
        <AlertTriangle className="mr-2 h-4 w-4" />Review Payee Changes
        <span className="ml-2 rounded-full bg-white/70 px-2 text-xs font-semibold" data-testid="review-payee-changes-count">{data ? pending : "…"}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid="payee-changes-dialog">
          <DialogHeader>
            <DialogTitle>Review payee changes</DialogTitle>
            <DialogDescription>Folios whose associate in the latest month differs from the month before, or whose statement rows disagree. Commission files are not built until every row is decided.</DialogDescription>
          </DialogHeader>
          {!data ? <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : data.rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="payee-changes-empty">No payee changes this month.</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border">
              <table className="w-full"><thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <tr><th className="px-3 py-2">Client</th><th className="px-3 py-2">Folio</th><th className="px-3 py-2">Last month</th><th className="px-3 py-2">This month</th><th className="px-3 py-2">Why</th><th className="px-3 py-2" /></tr>
              </thead><tbody>{data.rows.map((r) => <Row key={`${r.source}-${r.folio_no}`} r={r} busy={busy} decide={decide} />)}</tbody></table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
